import type { Right } from '@gozoki/tools'
import { useRouter } from '@tanstack/react-router'
import { useContext, type ReactNode } from 'react'
import { useAbility, type PermissionTuple } from '~/utils/ability'
import { AuthContext } from '~/utils/context/auth.context'
import {
  PermissionDeniedTooltip,
  resolveMissingPermissions,
  useRbacV2Enabled,
  type PermissionRequirement,
  type RouteTo,
} from '~/utils/rbac'

export interface GuardedTab<TValue extends string> {
  value: TValue
  label: ReactNode
  /** Route cible de l'onglet — son `staticData.permission` sert de source par défaut. */
  to?: RouteTo
  /** Override explicite quand l'onglet n'a pas de route dédiée. */
  permission?: PermissionRequirement
  /**
   * Droits v1 exigés tant que le flag `permission-v2` est OFF (où les tuples v2 passent tous).
   * À renseigner sur tout onglet dont la visibilité était gardée par `useRights(...)`.
   */
  legacyRights?: Right[]
}

export interface ResolvedTab<TValue extends string> extends GuardedTab<TValue> {
  authorized: boolean
  missingPermissions: PermissionTuple[]
  /** Prêt à poser dans un `Tooltip` ; `undefined` quand l'onglet est accessible. */
  tooltip: ReactNode | undefined
}

/**
 * Résout les droits de chaque onglet. Source par défaut : le `staticData.permission` de la route
 * cible — la permission n'est ainsi déclarée qu'à un seul endroit, la route.
 *
 * Règle produit : un onglet interdit est **désactivé** (avec tooltip), jamais masqué. Le hook
 * renvoie donc tous les onglets, enrichis de leur état d'autorisation.
 */
export const useAuthorizedTabs = <TValue extends string>(
  tabs: GuardedTab<TValue>[],
): ResolvedTab<TValue>[] => {
  const ability = useAbility()
  const router = useRouter()
  const { hasRights } = useContext(AuthContext)
  const v2Enabled = useRbacV2Enabled()

  // Lecture directe du registre de routes : `useRouteStaticData` est un hook et ne peut pas être
  // appelé dans le `map` ci-dessous.
  const routePermission = (to: RouteTo): PermissionRequirement | undefined => {
    // @ts-expect-error — `routesByPath` n'est pas typé côté public.
    const route = router.routesByPath[to] ?? router.routesByPath[`${to}/`]
    return route?.options?.staticData?.permission
  }

  return tabs.map((tab) => {
    const permission = tab.permission ?? (tab.to ? routePermission(tab.to) : undefined)
    const missingPermissions = resolveMissingPermissions(ability, permission)
    // Flag OFF : les tuples v2 passent tous, donc seul le bitmask legacy discrimine encore.
    const hasLegacyRequirement = !!tab.legacyRights && tab.legacyRights.length > 0
    const legacyAuthorized = hasLegacyRequirement
      ? hasRights(tab.legacyRights ?? [])
      : missingPermissions.length === 0
    // Flag ON : un onglet sans permission v2 déclarée n'est jamais ouvert par un bit legacy.
    const authorized = v2Enabled
      ? permission !== undefined && missingPermissions.length === 0
      : legacyAuthorized

    return {
      ...tab,
      authorized,
      missingPermissions,
      tooltip: authorized ? undefined : (
        <PermissionDeniedTooltip missingPermissions={missingPermissions} />
      ),
    }
  })
}

/** Première valeur autorisée — pour rediriger quand l'onglet actif devient interdit. */
export const useFirstAuthorizedTab = <TValue extends string>(
  tabs: ResolvedTab<TValue>[],
): TValue | undefined => tabs.find((tab) => tab.authorized)?.value
